<template>
  <DialogWrapper
    ref="changeCandidateStatusDialog"
    class="change-candidate-status-dialog"
    close-on-cancel
    :title="title"
    :show.sync="show"
    :loading="loading"
    @save="handleSave"
  >
    <WrappedForm
      ref="form"
      class="change-status-form"
      labelPosition="left"
      hideRequiredAsterisk
      :label-width="labelWidth"
      :model="statusChangeInfo"
      :formLines="formLines"
    >
    <template #statusList>
      <el-select v-model="statusChangeInfo.status" popper-class="status-select" filterable @change="onStatusChange">
        <el-option
          v-for="(item, index) in statusList"
          :key="index"
          :label="item.i18nName"
          :value="item.id"
          :disabled="item.disabled"
          :class="item.has_referral ? 'has-referral' : ''"
        >
          <span class="status-name">{{ item.i18nName }}</span>
          <i v-if="item.has_referral" class="iconfont iconhongbao1"></i>
        </el-option>
      </el-select>
    </template>
    <template #userEmail>
      <el-input
        v-model="statusChangeInfo.userEmail"
        :maxlength="64"
        :placeholder="$t(`${i18nPrefix}.placeholder.userEmail`)"
      >
        <template #append>{{ onRetailsUserEmailSuffix }}</template>
      </el-input>
    </template>
  </WrappedForm>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { addI18nProperty, isEn } from '@/i18n'
import DialogWrapper from '../../../../components/dialog/dialogWrapper.vue'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import moment from 'moment'
import { HIDE_STATUS, READY_TO_HIRE_STATUS_LIST, HIRED_STATUS, ON_RETAILS_EMPLOYEE_TYPE_LIST, ON_RETAILS_USER_EMAIL_SUFFIX } from '@/constant/application'
import { ROOT_COMPANY_ID } from '@/constant/company'

export default {
  components: {
    DialogWrapper,
    WrappedForm
  },
  props: {
    applicationList: {
      required: true
    },
    isAllApplication: {
      default: false
    },
    jobId: {
      required: false
    },
    searchParams: {
      required: false
    },
    loadedStatusList: {
      type: Array,
      default: null
    },
    defaultStatus: {
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      show: false,
      loading: false,
      statusChangeInfo: {},
      statusList: [],
      reasonList: [],
      toStatus: null,
      comment: null,
      hrList: [],
      onRetailsUserEmailSuffix: ON_RETAILS_USER_EMAIL_SUFFIX
    }
  },
  computed: {
    title () {
      let candidateName = null
      if (!this.isAllApplication && this.applicationList.length === 1) {
        const application = this.applicationList[0]
        candidateName = application?.candidate?.name
      }
      if (candidateName) {
        return this.$t(`${this.i18nPrefix}.dialogTitle.changeCandidateStatus`, { candidateName })
      } else {
        return this.$t(`${this.i18nPrefix}.dialogTitle.changeMultipleCandidateStatus`, { candidateName })
      }
    },
    labelWidth () {
      if (isEn()) {
        return '186px'
      } else {
        return '110px'
      }
    },
    isElcCompany () {
      return this.hasAnyPermission('COMPUTED_COMPANY_IS_ELC')
    },
    isMSDCompany () {
      const currentAccount = this.getCurrentAccount()
      const rootCompanyId = currentAccount.root_company_id
      return rootCompanyId === ROOT_COMPANY_ID.MSD
    },
    hasChangeStatusNoticeDesignatedRecruiterPermission () {
      return this.hasAnyPermission('PREMIUM_CANDIDATE_STATUS_UPDATE_NOTIFICATION_INTERNAL')
    },
    hasOnlySeeMyJobsPermission () {
      return this.hasAnyPermission('PERM_ONLY_SEE_MY_JOBS')
    },
    isOnRetailsApplication () {
      return this.applicationList.length > 0 &&
        _.every(this.applicationList, application => _.get(application, 'job.company.is_on_retails_company') === true)
    },
    showOnRetailsHireFields () {
      return this.isOnRetailsApplication && _.includes([HIRED_STATUS], this.statusChangeInfo.status)
    },
    formLines () {
      const vm = this
      const requireRule = { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }
      const today = moment().startOf('day')
      const lines = {
        status: {
          label: this.$t(`${this.i18nPrefix}.label.toStatus`),
          rules: [requireRule],
          contentSlot: 'statusList'
        },
        reason: {
          get hidden () {
            return vm.reasonList.length < 1
          },
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.changeCandidateStatusCause`),
          rules: [requireRule],
          option: {
            data: this.reasonList,
            labelKey: 'i18nName',
            valueKey: 'id'
          },
          props: {
            filterable: true,
            clearable: true
          }
        },
        onboardDate: {
          get hidden () {
            return !_.includes([HIRED_STATUS, ...READY_TO_HIRE_STATUS_LIST], vm.statusChangeInfo.status)
          },
          elType: 'el-date-picker',
          get label () { return vm.$t(`${vm.i18nPrefix}.label.${[HIRED_STATUS].includes(vm.statusChangeInfo.status) ? 'onboardDate' : 'hiredDate'}`) },
          props: {
            type: 'date',
            clearable: true,
            valueFormat: 'yyyy-MM-dd',
            pickerOptions: {
              disabledDate (time) {
                return today.isAfter(time)
              }
            }
          }
        },
        employeeType: {
          get hidden () {
            return !vm.showOnRetailsHireFields
          },
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.employeeType`),
          rules: [{ ...requireRule, trigger: 'change' }],
          option: {
            data: _.map(ON_RETAILS_EMPLOYEE_TYPE_LIST, value => ({ label: value, value }))
          },
          props: {
            filterable: true,
            clearable: true
          }
        },
        userEmail: {
          get hidden () {
            return !vm.showOnRetailsHireFields
          },
          label: this.$t(`${this.i18nPrefix}.label.userEmail`),
          contentSlot: 'userEmail',
          rules: [{ validator: this.validateUserEmail, trigger: 'blur' }]
        },
        endEmploymentDate: {
          get hidden () {
            return !vm.showOnRetailsHireFields || _.includes(['Store, Regular', 'Store, Self-employed', 'Store, Student'], vm.statusChangeInfo.employeeType)
          },
          elType: 'el-date-picker',
          label: this.$t(`${this.i18nPrefix}.label.endEmploymentDate`),
          rules: [{ validator: this.validateEndEmploymentDate, trigger: 'change' }],
          props: {
            type: 'date',
            clearable: true,
            valueFormat: 'yyyy-MM-dd',
            pickerOptions: {
              disabledDate (time) {
                return today.isAfter(time)
              }
            }
          }
        },
        compensationBasis: {
          get hidden () {
            return !vm.showOnRetailsHireFields
          },
          elType: 'el-input',
          label: this.$t(`${this.i18nPrefix}.label.compensationBasis`),
          rules: [{ validator: this.validateCompensationBasis, trigger: 'change' }],
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.compensationBasis`)
        },
        comment: {
          elType: 'el-input',
          label: this.$t(`${this.i18nPrefix}.label.changeComment`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.commentsPlaceholder`),
          props: {
            type: 'textarea',
            autosize: { minRows: 5, maxRows: 8 }
          }
        },
        noticeHr: {
          hidden: !this.hasChangeStatusNoticeDesignatedRecruiterPermission,
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.noticeRecruiter`),
          option: {
            data: this.hrList,
            labelKey: 'name',
            valueKey: 'id'
          },
          props: {
            multiple: true,
            filterable: true,
            clearable: true,
            multipleLimit: 5
          }
        }
      }
      return lines
    },
    form () {
      return this.$refs.form
    }
  },
  created () {

  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('application', ['changeCandidateStatus']),
    ...mapActions('candidateStatus', ['getCandidateStatusList', 'getStatusChangeReasonList']),
    ...mapActions('company', ['getCompanyHrList']),
    showDialog () {
      this.show = true
    },
    async refreshForm () {
      this.loading = true
      await Promise.all([
        this.loadCompanyHrList(),
        this.loadStatusList(),
        this.onStatusChange()
      ])
      if (_.includes(HIDE_STATUS, this.defaultStatus)) {
        this.statusChangeInfo = { status: this.applicationList[0].statusName }
      } else {
        this.statusChangeInfo = { status: this.defaultStatus }
      }
      if (this.defaultStatus && !_.includes(HIDE_STATUS, this.defaultStatus)) {
        await this.onStatusChange()
      }
      this.$nextTick(() => {
        if (this.form) {
          this.form.clearValidate()
        }
        this.loading = false
      })
    },
    async loadCompanyHrList () {
      const account = this.getCurrentAccount()
      const params = { companyId: account.company_id, exclude_disabled: true }
      const resp = await this.getCompanyHrList(params)
      if (resp) {
        this.hrList = _.orderBy(resp.results, hr => hr.name.toLowerCase(), 'asc')
      }
    },
    async loadStatusList () {
      if (this.loadedStatusList && this.loadedStatusList.length > 0) {
        this.statusList = _.filter(this.loadedStatusList, item => this.isMSDCompany ? !['Hired', 'Disqualified'].includes(item.key) : true)
      } else {
        const resp = await this.getCandidateStatusList({
          company_ids: _.map(this.applicationList, item => item.job.company.id).join(',')
        })
        if (resp) {
          this.statusList = _.chain(resp)
            .filter(item => this.isMSDCompany ? !['Hired', 'Disqualified'].includes(item.key) : true)
            .map(item => addI18nProperty(item, 'i18nName', 'name_cn', 'name_en'))
            .value()
        }
      }
      if (!this.isAllApplication && this.applicationList.length === 1) {
        const application = this.applicationList[0]
        const currentStatus = application?.status
        if (currentStatus && !_.includes(HIDE_STATUS, currentStatus.key) && !_.find(this.statusList, { id: currentStatus.key })) {
          const disabledStatus = {
            id: currentStatus.key,
            disabled: true,
            name_en: this.$t(`${this.i18nPrefix}.label.disabledStatus`, { status: currentStatus.name_en }),
            name_cn: this.$t(`${this.i18nPrefix}.label.disabledStatus`, { status: currentStatus.name_cn })
          }
          addI18nProperty(disabledStatus, 'i18nName', 'name_cn', 'name_en')
          this.statusList.push(disabledStatus)
        }
      }
    },
    async onStatusChange () {
      if (!this.statusChangeInfo.status) {
        return
      }
      if (!this.showOnRetailsHireFields) {
        this.$set(this.statusChangeInfo, 'employeeType', null)
        this.$set(this.statusChangeInfo, 'userEmail', null)
        this.$set(this.statusChangeInfo, 'endEmploymentDate', null)
        this.$set(this.statusChangeInfo, 'compensationBasis', null)
      }
      this.loading = true
      const resp = await this.getStatusChangeReasonList({
        status_key: this.statusChangeInfo.status
      })
      if (resp) {
        this.reasonList = _.map(resp, item => addI18nProperty(item, 'i18nName', 'name_cn', 'name_en'))
      }
      this.loading = false
    },
    validateEndEmploymentDate (rule, value, callback) {
      if (!value) {
        callback(new Error(this.$t('common.message.requiredFormField')))
        return
      }
      const hiredDate = this.statusChangeInfo.onboardDate
      if (hiredDate && moment(value).isBefore(moment(hiredDate), 'day')) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.endEmploymentDateBeforeHiredDate`)))
        return
      }
      callback()
    },
    validateCompensationBasis (rule, value, callback) {
      if (_.isNil(value)) {
        callback(new Error(this.$t('common.message.requiredFormField')))
        return
      }
      if (value <= 0) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.compensationBasisPositive`)))
        return
      }
      callback()
    },
    validateUserEmail (rule, value, callback) {
      const userEmailPrefix = _.trim(value)
      if (!userEmailPrefix) {
        callback(new Error(this.$t('common.message.requiredFormField')))
        return
      }
      if (!/^[A-Za-z0-9._-]+$/.test(userEmailPrefix)) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.userEmailInvalid`)))
        return
      }
      callback()
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      const valid = await this.form?.submit()
      if (!valid) {
        return
      }
      this.loading = true
      try {
        const statusChangeInfo = this.statusChangeInfo
        const userEmailPrefix = _.trim(statusChangeInfo.userEmail)
        const onRetailsHireInfo = this.showOnRetailsHireFields
          ? {
              employee_type: statusChangeInfo.employeeType,
              user_email: `${userEmailPrefix}${this.onRetailsUserEmailSuffix}`,
              end_employment_date: statusChangeInfo.endEmploymentDate,
              compensation_basis: String(statusChangeInfo.compensationBasis)
            }
          : {}
        let comments = statusChangeInfo.comment
        const onRetailFieldValues = [comments]
        if (this.showOnRetailsHireFields) {
          if (onRetailsHireInfo.employee_type) {
            onRetailFieldValues.push(`员工类型(Employee Type): ${onRetailsHireInfo.employee_type}`)
          }
          if (onRetailsHireInfo.user_email) {
            onRetailFieldValues.push(`用户邮箱(User Email): ${onRetailsHireInfo.user_email}`)
          }
          if (onRetailsHireInfo.end_employment_date) {
            onRetailFieldValues.push(`雇佣结束日期(Employment End Date): ${onRetailsHireInfo.end_employment_date}`)
          }
          if (onRetailsHireInfo.compensation_basis) {
            onRetailFieldValues.push(`薪酬基数(Compensation Basis): ${onRetailsHireInfo.compensation_basis}`)
          }
          comments = onRetailFieldValues.join('\n')
        }
        const data = {
          status_key: statusChangeInfo.status,
          reason_id: statusChangeInfo.reason,
          onboard_date: statusChangeInfo.onboardDate,
          comment: comments,
          is_all_application: this.isAllApplication,
          application_ids: this.isAllApplication ? null : _.map(this.applicationList, 'id'),
          job_id: this.jobId,
          search_params: this.searchParams,
          notice_hr_ids: statusChangeInfo.noticeHr,
          operation_page_url: window.location.href,
          ...onRetailsHireInfo
        }
        const resp = await this.changeCandidateStatus(data)
        if (resp) {
          const isOneApplication = this.applicationList.length === 1
          const consentFailedCount = resp.failed_application_ids?.consent_status_management?.length || 0
          const sameChangeRecordCount = resp.failed_application_ids?.same_change_record?.length || 0
          const nextReferralStepErrorCount = resp.failed_application_ids?.next_referral_step_error?.length || 0
          const isFinalReferralStepCount = resp.failed_application_ids?.is_final_referral_step?.length || 0
          const successCount = resp.success_application_ids?.length || 0
          if (isOneApplication) {
            if (consentFailedCount) {
              this.$message.error(this.$t(`${this.i18nPrefix}.message.changeStatusResult.consentFailed`))
            } else if (sameChangeRecordCount) {
              this.$message.error(this.$t(`${this.i18nPrefix}.message.changeStatusResult.sameChangeRecord`))
            } else if (nextReferralStepErrorCount || isFinalReferralStepCount) {
              this.$message.error(this.$t(`${this.i18nPrefix}.message.changeStatusResult.referralError`))
            } else if (successCount) {
              this.$message.success(this.$t(`${this.i18nPrefix}.message.changeStatusResult.success`))
              this.show = false
              _.forEach(this.applicationList, application => {
                application.status = _.find(this.statusList, { key: statusChangeInfo.status })
              })
              this.$emit('success', resp, statusChangeInfo)
            }
          } else {
            const failedCount = consentFailedCount + sameChangeRecordCount + nextReferralStepErrorCount + isFinalReferralStepCount
            if (failedCount > 0) {
              const messageList = [
                this.$t(`${this.i18nPrefix}.message.changeStatusResult.batch.failed.base`, { success: successCount, failed: failedCount })
              ]
              const detailList = []
              if (consentFailedCount) {
                detailList.push(this.$t(`${this.i18nPrefix}.message.changeStatusResult.batch.failed.consentFailed`, { consentFailed: consentFailedCount }))
              }
              if (sameChangeRecordCount) {
                detailList.push(this.$t(`${this.i18nPrefix}.message.changeStatusResult.batch.failed.sameChangeRecord`, { sameChangeRecord: sameChangeRecordCount }))
              }
              if (nextReferralStepErrorCount || isFinalReferralStepCount) {
                detailList.push(this.$t(`${this.i18nPrefix}.message.changeStatusResult.batch.failed.referralError`, { referralError: nextReferralStepErrorCount + isFinalReferralStepCount }))
              }
              if (detailList.length) {
                const detail = detailList.join(this.$t(`${this.i18nPrefix}.message.changeStatusResult.batch.failed.join`)) + this.$t(`${this.i18nPrefix}.message.changeStatusResult.batch.failed.end`)
                messageList.push(detail)
              }
              this.$message({
                type: 'warning',
                dangerouslyUseHTMLString: true,
                message: messageList.join('')
              })
            } else {
              this.$message.success(this.$t(`${this.i18nPrefix}.message.changeStatusResult.batch.success`))
            }
            this.show = false
            if (successCount > 0) {
              this.$emit('success', resp, statusChangeInfo)
            }
          }
        }
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    show () {
      if (!this.show) {
        if (this.form) {
          this.form.clearValidate()
        }
        this.statusChangeInfo = {}
        return
      }
      this.refreshForm()
    }
  }
}

</script>

<style lang='scss' scoped>
.change-candidate-status-dialog {
  .change-status-form {

  }
}

.status-select {
  .has-referral {

    .status-name {
      vertical-align: middle;
    }

    .iconfont {
      font-size: 24px;
      vertical-align: middle;

      &::before {
        display: inline-block;
        margin-bottom: 5px;
      }
    }
  }
}
</style>
